import { jsxs, jsx } from "react/jsx-runtime";
import { useContext, useRef, useEffect, cloneElement } from "react";
import assert from "assert";
import { Box } from "../Box/Box.mjs";
import { normalizeKey } from "../private/normalizeKey.mjs";
import { TabsContext } from "./TabsProvider.mjs";
import { TAB_BUTTON_REGISTER, TAB_BUTTON_CLICK, TAB_LIST_FOCUSED, TAB_BUTTON_TAB, TAB_BUTTON_RIGHT, TAB_BUTTON_LEFT, TAB_BUTTON_HOME, TAB_BUTTON_END, TAB_BUTTON_ENTER, TAB_BUTTON_SPACE } from "./Tabs.actions.mjs";
import { Text } from "../Text/Text.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import { TabListContext } from "./TabListContext.mjs";
import { Overlay } from "../private/Overlay/Overlay.mjs";
import { useResponsiveValue } from "../useResponsiveValue/useResponsiveValue.mjs";
import { smoothScroll, smoothScrollIntoView } from "../private/smoothScroll.mjs";
import { useSpace } from "../useSpace/useSpace.mjs";
import { tab, hoveredTab, tabFocusRing } from "./Tabs.css.mjs";
const dividerSpacingForSize = {
  standard: "medium",
  small: "small"
};
const paddingXForTabSize = {
  standard: "small",
  small: "xsmall"
};
const Tab = ({ children, data, badge, icon, item, ...restProps }) => {
  const tabsContext = useContext(TabsContext);
  const tabListContext = useContext(TabListContext);
  const tabRef = useRef(null);
  assert(
    tabListContext !== null,
    "A Tab must be rendered as a child of Tabs. See the documentation for correct usage: https://design-system.machobear.ca"
  );
  assert(
    // @ts-expect-error
    !badge || badge.type.__isBadge__,
    `Tab badge prop can only be an instance of Badge. e.g. <Tab badge={<Badge>New</Badge>}>`
  );
  assert(
    !badge || badge.props.bleedY === void 0,
    "Badge elements cannot set the 'bleedY' prop when passed to a Tab component"
  );
  assert(
    !icon || icon.props.size === void 0 && icon.props.tone === void 0,
    "Icons cannot set the 'size' or 'tone' prop when passed to a Tab component"
  );
  if (!tabListContext) {
    throw new Error("Tab rendered outside Tabs context");
  }
  if (!tabsContext) {
    throw new Error("Tab rendered outside TabsProvider");
  }
  const { focusedTabIndex, selectedIndex, selectedItem, dispatch, a11y, onChange } = tabsContext;
  const { tabListItemIndex, scrollContainer, isLast, size } = tabListContext;
  const isSelected = selectedIndex > -1 ? selectedIndex === tabListItemIndex : selectedItem === item;
  const isFocused = focusedTabIndex === tabListItemIndex;
  const { grid, space } = useSpace();
  useEffect(() => {
    if (tabRef.current && isFocused) {
      tabRef.current.focus();
    }
  }, [isFocused]);
  const firstRenderRef = useRef(true);
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  });
  useEffect(() => {
    if (!tabRef.current || !scrollContainer) {
      return;
    }
    if (isSelected || isFocused) {
      if (isMobile) {
        smoothScroll(tabRef.current, {
          scrollContainer,
          direction: "horizontal",
          offset: space[paddingXForTabSize[size]] * grid * 3,
          ...firstRenderRef.current ? { duration: 0 } : { speed: 0.7 }
        });
      } else {
        smoothScrollIntoView(tabRef.current, {
          scrollContainer,
          direction: "horizontal",
          offset: space[paddingXForTabSize[size]] * grid * 6,
          ...firstRenderRef.current ? { duration: 0 } : { speed: 0.7 }
        });
      }
    }
    firstRenderRef.current = false;
  }, [isSelected, isFocused, scrollContainer, space, grid, isMobile, size]);
  useEffect(() => {
    if (tabRef.current) {
      dispatch({
        type: TAB_BUTTON_REGISTER,
        tabEl: tabRef.current,
        tabListItemIndex
      });
    }
  }, [dispatch, tabListItemIndex]);
  const onKeyUp = (event) => {
    const targetKey = normalizeKey(event);
    if (targetKey === "Tab") {
      return;
    }
    if (onChange && (targetKey === "Enter" || targetKey === " ")) {
      onChange(tabListItemIndex, item);
    }
    const action = {
      ArrowRight: { type: TAB_BUTTON_RIGHT },
      ArrowLeft: { type: TAB_BUTTON_LEFT },
      Home: { type: TAB_BUTTON_HOME },
      End: { type: TAB_BUTTON_END },
      Enter: { type: TAB_BUTTON_ENTER, value: tabListItemIndex },
      " ": { type: TAB_BUTTON_SPACE, value: tabListItemIndex }
    };
    if (action[targetKey]) {
      dispatch(action[targetKey]);
    }
  };
  const onKeyDown = (event) => {
    const targetKey = normalizeKey(event);
    if (targetKey === "Tab" && focusedTabIndex !== null) {
      dispatch({ type: TAB_BUTTON_TAB });
      return;
    }
    const isArrowPress = targetKey.indexOf("Arrow") === 0;
    const isActionKeyPress = targetKey === "Enter" || targetKey === " ";
    if (isArrowPress || isActionKeyPress) {
      event.preventDefault();
    }
  };
  return /* @__PURE__ */ jsxs(
    Box,
    {
      component: "button",
      type: "button",
      ...a11y.tabProps({ tabIndex: tabListItemIndex, isSelected }),
      ref: tabRef,
      onKeyUp,
      onKeyDown,
      onClick: (event) => {
        event.stopPropagation();
        event.preventDefault();
        if (onChange) {
          onChange(tabListItemIndex, item);
        }
        dispatch({ type: TAB_BUTTON_CLICK, value: tabListItemIndex });
      },
      onFocus: isSelected ? () => dispatch({
        type: TAB_LIST_FOCUSED,
        value: tabListItemIndex
      }) : void 0,
      display: "flex",
      alignItems: "center",
      textAlign: "left",
      borderRadius: "standard",
      cursor: "pointer",
      outline: "none",
      position: "relative",
      zIndex: 1,
      paddingLeft: tabListItemIndex > 0 ? paddingXForTabSize[size] : void 0,
      paddingRight: !isLast ? paddingXForTabSize[size] : void 0,
      paddingY: dividerSpacingForSize[size],
      className: tab,
      ...buildDataAttributes({ data, validateRestProps: restProps }),
      children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            component: "span",
            display: "block",
            position: "absolute",
            "aria-hidden": true,
            transition: "fast",
            opacity: isSelected ? 0 : void 0,
            children: /* @__PURE__ */ jsx(Text, { size, tone: "secondary", icon, children })
          }
        ),
        /* @__PURE__ */ jsx(
          Box,
          {
            component: "span",
            display: "block",
            position: "absolute",
            "aria-hidden": true,
            transition: "fast",
            opacity: 0,
            className: !isSelected ? hoveredTab : void 0,
            children: /* @__PURE__ */ jsx(Text, { size, icon, children })
          }
        ),
        /* @__PURE__ */ jsx(Box, { component: "span", display: "block", transition: "fast", opacity: !isSelected ? 0 : void 0, children: /* @__PURE__ */ jsx(
          Text,
          {
            ...a11y.tabLabelProps({ tabIndex: tabListItemIndex }),
            size,
            tone: "formAccent",
            icon,
            children
          }
        ) }),
        badge ? /* @__PURE__ */ jsx(Box, { component: "span", paddingLeft: "xsmall", children: cloneElement(badge, { bleedY: true }) }) : null,
        /* @__PURE__ */ jsx(
          Overlay,
          {
            component: "span",
            zIndex: 1,
            boxShadow: "outlineFocus",
            borderRadius: "standard",
            className: tabFocusRing,
            visible: false,
            transition: "fast",
            onlyVisibleForKeyboardNavigation: true
          }
        )
      ]
    }
  );
};
Tab.__isTab__ = true;
export {
  Tab,
  dividerSpacingForSize
};
